/**
 * Hit Rate Gauge Component
 * A doughnut/gauge chart showing cache hit rate percentage
 */

import { Card } from '@/components/ui/Card';
import { useI18n } from '@/i18n';

export interface HitRateGaugeProps {
  hitRate: number; // 0-1
  className?: string;
}

function getHitRateColor(hitRate: number): string {
  if (hitRate >= 0.8) return 'hsl(var(--success))';
  if (hitRate >= 0.5) return 'hsl(var(--warning))';
  return 'hsl(var(--destructive))';
}

export function HitRateGauge({ hitRate, className = '' }: HitRateGaugeProps) {
  const { messages } = useI18n();
  const t = messages.monitoring.charts;

  const percentage = hitRate * 100;
  const color = getHitRateColor(hitRate);

  // SVG circle parameters
  const size = 160;
  const strokeWidth = 20;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (hitRate * circumference);

  return (
    <Card className={`${className}`}>
      <h3 className="text-sm font-medium text-muted-foreground mb-4">{t.cacheHitRate}</h3>
      <div className="relative flex items-center justify-center" style={{ height: 160 }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="transform -rotate-90"
        >
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="hsl(var(--muted))"
            strokeWidth={strokeWidth}
          />
          {/* Foreground circle (hit rate) */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-500 ease-out"
          />
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <span className="text-3xl font-bold text-foreground">
              {percentage.toFixed(1)}%
            </span>
            <p className="text-xs text-muted-foreground">{t.hitRateLabel}</p>
          </div>
        </div>
      </div>
    </Card>
  );
}
